import { gql } from '@apollo/client';

// ─── AUTH ────────────────────────────────────────────────────────────────────
export const REGISTER = gql`
  mutation Register($name: String!, $email: String!, $password: String!, $college: String, $branch: String, $year: Int) {
    register(name: $name, email: $email, password: $password, college: $college, branch: $branch, year: $year) {
      token
      user { id name email college branch year skills }
    }
  }
`;

export const LOGIN = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      token
      user { id name email college branch year skills bio }
    }
  }
`;

export const GOOGLE_LOGIN = gql`
  mutation GoogleLogin($idToken: String!) {
    googleLogin(idToken: $idToken) {
      token
      user { id name email college branch year skills bio }
    }
  }
`;

export const FORGOT_PASSWORD = gql`
  mutation ForgotPassword($email: String!) {
    forgotPassword(email: $email)
  }
`;

export const RESET_PASSWORD = gql`
  mutation ResetPassword($token: String!, $newPassword: String!) {
    resetPassword(token: $token, newPassword: $newPassword)
  }
`;

export const ME = gql`
  query Me {
    me { id name email college branch year bio skills avatarUrl createdAt section enrolledSubjects hobbies lookingFor openToConnect matchVisibility city coinBalance }
  }
`;

// ─── ATTENDANCE ──────────────────────────────────────────────────────────────
export const MY_SUBJECTS = gql`
  query MySubjects {
    mySubjects { id name totalClasses attendedClasses percentage requiredToReach75 canBunk }
  }
`;

export const ADD_SUBJECT = gql`
  mutation AddSubject($name: String!, $totalClasses: Int!, $attendedClasses: Int!) {
    addSubject(name: $name, totalClasses: $totalClasses, attendedClasses: $attendedClasses) {
      id name totalClasses attendedClasses percentage requiredToReach75 canBunk
    }
  }
`;

export const UPDATE_ATTENDANCE = gql`
  mutation UpdateAttendance($subjectId: ID!, $totalClasses: Int!, $attendedClasses: Int!) {
    updateAttendance(subjectId: $subjectId, totalClasses: $totalClasses, attendedClasses: $attendedClasses) {
      id name totalClasses attendedClasses percentage requiredToReach75 canBunk
    }
  }
`;

export const DELETE_SUBJECT = gql`
  mutation DeleteSubject($subjectId: ID!) { deleteSubject(subjectId: $subjectId) }
`;

// ─── CGPA ────────────────────────────────────────────────────────────────────
export const MY_CGPA = gql`
  query MyCgpa {
    myCgpa {
      cgpa
      semesters {
        semester sgpa
        courses { id name credits grade gradePoints }
      }
    }
  }
`;

export const SAVE_COURSES = gql`
  mutation SaveCourses($courses: [CourseInput!]!) {
    saveCourses(courses: $courses) {
      cgpa
      semesters { semester sgpa courses { id name credits grade gradePoints } }
    }
  }
`;

// ─── EXAMS ───────────────────────────────────────────────────────────────────
export const MY_EXAMS = gql`
  query MyExams { myExams { id title subject examDate daysLeft notes } }
`;

export const ADD_EXAM = gql`
  mutation AddExam($title: String!, $subject: String!, $examDate: DateTime!, $notes: String) {
    addExam(title: $title, subject: $subject, examDate: $examDate, notes: $notes) {
      id title subject examDate daysLeft notes
    }
  }
`;

export const DELETE_EXAM = gql`
  mutation DeleteExam($examId: ID!) { deleteExam(examId: $examId) }
`;

// ─── NOTES ───────────────────────────────────────────────────────────────────
const NOTE_FIELDS = `
  id title subject semester fileUrl fileType downloads tags createdAt date
  chapter className noteType description pageCount savedCount hasSaved
  pages { id imageUrl pageNumber caption }
  uploadedBy { id name college year avatarUrl }
`;

export const NOTES = gql`
  query Notes($subject: String, $semester: Int, $search: String, $date: String, $chapter: String, $noteType: NoteType, $savedOnly: Boolean) {
    notes(subject: $subject, semester: $semester, search: $search, date: $date, chapter: $chapter, noteType: $noteType, savedOnly: $savedOnly) {
      ${NOTE_FIELDS}
    }
  }
`;

export const UPLOAD_NOTE = gql`
  mutation UploadNote($title: String!, $subject: String!, $semester: Int!, $tags: [String!], $date: String, $chapter: String, $className: String, $noteType: NoteType, $description: String, $pages: [NotePageInput!]) {
    uploadNote(title: $title, subject: $subject, semester: $semester, tags: $tags, date: $date, chapter: $chapter, className: $className, noteType: $noteType, description: $description, pages: $pages) {
      ${NOTE_FIELDS}
    }
  }
`;

export const ADD_NOTE_PAGES = gql`
  mutation AddNotePages($noteId: ID!, $pages: [NotePageInput!]!) {
    addNotePages(noteId: $noteId, pages: $pages) { ${NOTE_FIELDS} }
  }
`;

export const DELETE_NOTE = gql`
  mutation DeleteNote($noteId: ID!) { deleteNote(noteId: $noteId) }
`;

export const SAVE_NOTE = gql`
  mutation SaveNote($noteId: ID!) { saveNote(noteId: $noteId) }
`;

export const INCREMENT_DOWNLOAD = gql`
  mutation IncrementDownload($noteId: ID!) { incrementDownload(noteId: $noteId) }
`;

// ─── FLASHCARDS ──────────────────────────────────────────────────────────────
export const MY_DECKS = gql`
  query MyDecks {
    myFlashcardDecks {
      id title subject createdAt
      cards { id question answer topic }
    }
  }
`;

export const GENERATE_FLASHCARDS = gql`
  mutation GenerateFlashcards($text: String!, $title: String!, $subject: String) {
    generateFlashcardsFromText(text: $text, title: $title, subject: $subject) {
      deck { id title subject cards { id question answer topic } }
      quiz { question options correctIndex explanation }
      revisionSheet
    }
  }
`;

// ─── PROJECTS ────────────────────────────────────────────────────────────────
export const PROJECTS = gql`
  query Projects($type: ProjectType, $skill: String) {
    projects(type: $type, skill: $skill) {
      id title description type skillsNeeded teamSize currentMembers deadline createdAt
      creator { id name avatarUrl college branch }
      members { id name avatarUrl }
    }
  }
`;

export const MY_PROJECTS = gql`
  query MyProjects {
    myProjects {
      id title description type skillsNeeded teamSize currentMembers
      creator { id name }
      members { id name avatarUrl }
    }
  }
`;

export const CREATE_PROJECT = gql`
  mutation CreateProject($title: String!, $description: String!, $type: ProjectType!, $skillsNeeded: [String!]!, $teamSize: Int!, $deadline: DateTime) {
    createProject(title: $title, description: $description, type: $type, skillsNeeded: $skillsNeeded, teamSize: $teamSize, deadline: $deadline) {
      id title type skillsNeeded teamSize currentMembers createdAt
    }
  }
`;

export const APPLY_TO_PROJECT = gql`
  mutation Apply($projectId: ID!, $message: String) {
    applyToProject(projectId: $projectId, message: $message) { id status }
  }
`;

// ─── EXPENSES ────────────────────────────────────────────────────────────────
export const MY_EXPENSE_GROUPS = gql`
  query MyExpenseGroups {
    myExpenseGroups {
      id name totalExpenses createdAt
      members { id name avatarUrl }
      balances { user { id name } owes isOwed net }
      expenses {
        id description amount category date
        paidBy { id name }
        splits { user { id name } amount settled }
      }
    }
  }
`;

export const CREATE_EXPENSE_GROUP = gql`
  mutation CreateGroup($name: String!, $memberEmails: [String!]!) {
    createExpenseGroup(name: $name, memberEmails: $memberEmails) {
      id name members { id name }
    }
  }
`;

export const ADD_EXPENSE = gql`
  mutation AddExpense($groupId: ID!, $description: String!, $amount: Float!, $splitWith: [ID!]!, $category: String!) {
    addExpense(groupId: $groupId, description: $description, amount: $amount, splitWith: $splitWith, category: $category) {
      id description amount category date paidBy { id name }
    }
  }
`;

// ─── MARKETPLACE ─────────────────────────────────────────────────────────────
export const LISTINGS = gql`
  query Listings($category: String, $search: String, $maxPrice: Float) {
    listings(category: $category, search: $search, maxPrice: $maxPrice) {
      id title description price category condition imageUrls status contactInfo createdAt
      seller { id name avatarUrl college }
    }
  }
`;

export const CREATE_LISTING = gql`
  mutation CreateListing($title: String!, $description: String!, $price: Float!, $category: String!, $condition: String!, $contactInfo: String!, $imageUrls: [String!]) {
    createListing(title: $title, description: $description, price: $price, category: $category, condition: $condition, contactInfo: $contactInfo, imageUrls: $imageUrls) {
      id title price status
    }
  }
`;

// ─── TIMETABLE ───────────────────────────────────────────────────────────────
export const MY_TIMETABLE = gql`
  query MyTimetable($semester: Int) {
    myTimetable(semester: $semester) {
      id semester createdAt
      slots { id day startTime endTime title type location color }
    }
  }
`;

export const GENERATE_AI_TIMETABLE = gql`
  mutation GenerateTimetable($rawSchedule: String!, $constraints: String) {
    generateAiTimetable(rawSchedule: $rawSchedule, constraints: $constraints) {
      id semester slots { id day startTime endTime title type location color }
    }
  }
`;

// ─── ANNOUNCEMENTS ───────────────────────────────────────────────────────────
export const ANNOUNCEMENTS = gql`
  query Announcements($type: AnnouncementType, $subject: String, $college: String, $branch: String) {
    announcements(type: $type, subject: $subject, college: $college, branch: $branch) {
      id title body type subject pinned createdAt college branch views
      author { id name college branch }
    }
  }
`;

export const POST_ANNOUNCEMENT = gql`
  mutation PostAnnouncement($title: String!, $body: String!, $type: AnnouncementType!, $subject: String, $pinned: Boolean) {
    postAnnouncement(title: $title, body: $body, type: $type, subject: $subject, pinned: $pinned) {
      id title body type subject pinned createdAt author { id name }
    }
  }
`;

export const NEW_ANNOUNCEMENT_SUB = gql`
  subscription { newAnnouncement { id title body type subject pinned createdAt author { id name } } }
`;

// ─── THE LOOP ────────────────────────────────────────────────────────────────
export const KNOWLEDGE_POSTS = gql`
  query KnowledgePosts($category: KnowledgeCategory, $subject: String, $questionsOnly: Boolean) {
    knowledgePosts(category: $category, subject: $subject, questionsOnly: $questionsOnly) {
      id category title body subject professor upvotes hasUpvoted createdAt isQuestion
      author { id name year branch }
      replies { id body createdAt author { id name year branch } }
    }
  }
`;

export const CREATE_KNOWLEDGE_POST = gql`
  mutation CreateKnowledgePost($category: KnowledgeCategory!, $title: String!, $body: String!, $subject: String, $professor: String, $isQuestion: Boolean) {
    createKnowledgePost(category: $category, title: $title, body: $body, subject: $subject, professor: $professor, isQuestion: $isQuestion) {
      id category title upvotes hasUpvoted createdAt isQuestion author { id name }
      replies { id body createdAt author { id name } }
    }
  }
`;

export const UPVOTE_POST = gql`
  mutation UpvotePost($postId: ID!) { upvoteKnowledgePost(postId: $postId) { id upvotes hasUpvoted } }
`;

export const REPLY_TO_POST = gql`
  mutation ReplyToPost($postId: ID!, $body: String!) {
    replyToPost(postId: $postId, body: $body) { id body createdAt author { id name year branch } }
  }
`;

// ─── PLACEMENT ───────────────────────────────────────────────────────────────
const COMPANY_FIELDS = `
  id name roles minCgpa branches maxBacklogs packageLpa visitDate applyLink process
  isEligible isWishlisted createdAt
  valuesText benefits internshipProcess jobSiteUrl
  focusSubjects skillsRequired interviewRounds
  packageBreakdown { ctc base variable bonus esop joiningBonus }
`;

export const PLACEMENT_COMPANIES = gql`
  query PlacementCompanies($eligibleOnly: Boolean, $wishlisted: Boolean) {
    placementCompanies(eligibleOnly: $eligibleOnly, wishlisted: $wishlisted) { ${COMPANY_FIELDS} }
  }
`;

export const ADD_PLACEMENT_COMPANY = gql`
  mutation AddPlacementCompany($name: String!, $roles: [String!]!, $minCgpa: Float!, $branches: [String!]!, $maxBacklogs: Int!, $packageLpa: String, $visitDate: String, $applyLink: String, $process: String) {
    addPlacementCompany(name: $name, roles: $roles, minCgpa: $minCgpa, branches: $branches, maxBacklogs: $maxBacklogs, packageLpa: $packageLpa, visitDate: $visitDate, applyLink: $applyLink, process: $process) {
      ${COMPANY_FIELDS}
    }
  }
`;

export const UPDATE_COMPANY_DETAILS = gql`
  mutation UpdateCompanyDetails($companyId: ID!, $valuesText: String, $benefits: String, $internshipProcess: String, $jobSiteUrl: String, $focusSubjects: [String!], $skillsRequired: [String!], $interviewRounds: [String!], $packageBreakdown: PackageBreakdownInput) {
    updateCompanyDetails(companyId: $companyId, valuesText: $valuesText, benefits: $benefits, internshipProcess: $internshipProcess, jobSiteUrl: $jobSiteUrl, focusSubjects: $focusSubjects, skillsRequired: $skillsRequired, interviewRounds: $interviewRounds, packageBreakdown: $packageBreakdown) {
      ${COMPANY_FIELDS}
    }
  }
`;

export const TOGGLE_WISHLIST = gql`
  mutation ToggleWishlist($companyId: ID!) { toggleWishlist(companyId: $companyId) }
`;

export const APPLY_TO_COMPANY = gql`
  mutation ApplyToCompany($companyId: ID!) { applyToCompany(companyId: $companyId) { id status appliedAt } }
`;

export const MY_PLACEMENT_APPS = gql`
  query MyPlacementApps { myPlacementApplications { id status appliedAt company { id name packageLpa } } }
`;

export const PLACEMENT_RESOURCES = gql`
  query PlacementResources($companyId: ID, $resourceType: ResourceType, $topic: String) {
    placementResources(companyId: $companyId, resourceType: $resourceType, topic: $topic) {
      id companyId resourceType title url content difficulty topic createdAt
      questions { id question options correctIndex explanation }
    }
  }
`;

export const MOCK_TEST = gql`
  query MockTest($resourceId: ID!) {
    mockTest(resourceId: $resourceId) {
      id title difficulty topic
      questions { id question options correctIndex explanation }
    }
  }
`;

export const ADD_PLACEMENT_RESOURCE = gql`
  mutation AddPlacementResource($companyId: ID, $resourceType: ResourceType!, $title: String!, $url: String, $content: String, $difficulty: String, $topic: String) {
    addPlacementResource(companyId: $companyId, resourceType: $resourceType, title: $title, url: $url, content: $content, difficulty: $difficulty, topic: $topic) {
      id resourceType title url difficulty topic
    }
  }
`;

export const ADD_MOCK_QUESTION = gql`
  mutation AddMockQuestion($resourceId: ID!, $question: String!, $options: [String!]!, $correctIndex: Int!, $explanation: String) {
    addMockQuestion(resourceId: $resourceId, question: $question, options: $options, correctIndex: $correctIndex, explanation: $explanation) {
      id question options correctIndex explanation
    }
  }
`;

// ─── RESUME BUILDER ───────────────────────────────────────────────────────────
export const MY_RESUME = gql`
  query MyResume { myResume { id template data updatedAt } }
`;
export const SAVE_RESUME = gql`
  mutation SaveResume($template: String!, $data: String!) {
    saveResume(template: $template, data: $data) { id template data updatedAt }
  }
`;

// ─── LINKEDIN BUILDER ─────────────────────────────────────────────────────────
export const MY_LINKEDIN = gql`
  query MyLinkedIn { myLinkedInProfile { id data updatedAt } }
`;
export const SAVE_LINKEDIN = gql`
  mutation SaveLinkedIn($data: String!) {
    saveLinkedInProfile(data: $data) { id data updatedAt }
  }
`;

// ─── DOCUMENT VAULT ───────────────────────────────────────────────────────────
export const MY_DOCUMENTS = gql`
  query MyDocuments($docType: DocType) {
    myDocuments(docType: $docType) { id docType title issuer issueDate expiryDate referenceMasked fileUrl notes isSensitive createdAt }
  }
`;
export const ADD_DOCUMENT = gql`
  mutation AddDocument($docType: DocType!, $title: String!, $issuer: String, $issueDate: String, $expiryDate: String, $referenceMasked: String, $fileUrl: String, $notes: String, $isSensitive: Boolean) {
    addDocument(docType: $docType, title: $title, issuer: $issuer, issueDate: $issueDate, expiryDate: $expiryDate, referenceMasked: $referenceMasked, fileUrl: $fileUrl, notes: $notes, isSensitive: $isSensitive) {
      id docType title issuer issueDate expiryDate referenceMasked fileUrl notes isSensitive createdAt
    }
  }
`;
export const DELETE_DOCUMENT = gql`
  mutation DeleteDocument($documentId: ID!) { deleteDocument(documentId: $documentId) }
`;

// ─── ACADEMIC HEALTH ─────────────────────────────────────────────────────────
export const MY_ACADEMIC_HEALTH = gql`
  query MyAcademicHealth {
    myAcademicHealth {
      overallStatus cgpa activeBacklogs alerts detentionRisk
      subjectHealth { status alertMessage subject { id name percentage totalClasses attendedClasses } }
    }
  }
`;

export const MY_BACKLOGS = gql`
  query MyBacklogs { myBacklogs { id subject semester cleared } }
`;

export const ADD_BACKLOG = gql`
  mutation AddBacklog($subject: String!, $semester: Int!) { addBacklog(subject: $subject, semester: $semester) { id subject semester cleared } }
`;

export const CLEAR_BACKLOG = gql`
  mutation ClearBacklog($backlogId: ID!) { clearBacklog(backlogId: $backlogId) { id cleared } }
`;

// ─── GRIEVANCES ──────────────────────────────────────────────────────────────
export const MY_GRIEVANCES = gql`
  query MyGrievances { myGrievances { id category title description status adminNote createdAt updatedAt } }
`;

export const SUBMIT_GRIEVANCE = gql`
  mutation SubmitGrievance($category: String!, $title: String!, $description: String!) {
    submitGrievance(category: $category, title: $title, description: $description) {
      id category title status createdAt
    }
  }
`;

// ─── SYLLABUS ────────────────────────────────────────────────────────────────
export const SYLLABUS = gql`
  query Syllabus($subject: String, $semester: Int, $branch: String) {
    syllabus(subject: $subject, semester: $semester, branch: $branch) {
      id subject semester branch unit title topics resources { label url } createdAt
    }
  }
`;

export const ADD_SYLLABUS_ITEM = gql`
  mutation AddSyllabusItem($subject: String!, $semester: Int!, $branch: String, $unit: Int!, $title: String!, $topics: [String!]!, $resources: [ResourceInput!]) {
    addSyllabusItem(subject: $subject, semester: $semester, branch: $branch, unit: $unit, title: $title, topics: $topics, resources: $resources) {
      id subject unit title topics
    }
  }
`;

// ─── SPORTS ──────────────────────────────────────────────────────────────────
export const SPORTS_EVENTS = gql`
  query SportsEvents($sport: String, $status: String) {
    sportsEvents(sport: $sport, status: $status) {
      id title sport description organizer { id name college }
      eventDate venue maxParticipants registrationDeadline status result college
      participantCount participants { id name college skills }
      createdAt
    }
  }
`;
export const MY_SPORTS_REGISTRATIONS = gql`
  query MySportsRegistrations { mySportsRegistrations { id title sport eventDate venue status participantCount createdAt } }
`;
export const CREATE_SPORTS_EVENT = gql`
  mutation CreateSportsEvent($title: String!, $sport: String!, $description: String, $eventDate: String, $venue: String, $maxParticipants: Int, $registrationDeadline: String, $college: String) {
    createSportsEvent(title: $title, sport: $sport, description: $description, eventDate: $eventDate, venue: $venue, maxParticipants: $maxParticipants, registrationDeadline: $registrationDeadline, college: $college) {
      id title sport status participantCount createdAt
    }
  }
`;
export const REGISTER_FOR_SPORTS_EVENT = gql`
  mutation RegisterForSportsEvent($eventId: ID!, $teamName: String) {
    registerForSportsEvent(eventId: $eventId, teamName: $teamName)
  }
`;
export const UPDATE_SPORTS_RESULT = gql`
  mutation UpdateSportsResult($eventId: ID!, $result: String!, $status: String) {
    updateSportsResult(eventId: $eventId, result: $result, status: $status) { id status result }
  }
`;

// ─── SHOWCASE ─────────────────────────────────────────────────────────────────
export const SHOWCASE_PROJECTS = gql`
  query ShowcaseProjects($tag: String, $search: String, $college: String) {
    showcaseProjects(tag: $tag, search: $search, college: $college) {
      id title description author { id name college avatarUrl }
      techStack githubUrl liveUrl tags college likes hasLiked createdAt
    }
  }
`;
export const MY_SHOWCASE_PROJECTS = gql`
  query MyShowcaseProjects { myShowcaseProjects { id title description techStack githubUrl liveUrl tags likes createdAt } }
`;
export const CREATE_SHOWCASE_PROJECT = gql`
  mutation CreateShowcaseProject($title: String!, $description: String!, $techStack: [String!], $githubUrl: String, $liveUrl: String, $tags: [String!], $college: String) {
    createShowcaseProject(title: $title, description: $description, techStack: $techStack, githubUrl: $githubUrl, liveUrl: $liveUrl, tags: $tags, college: $college) {
      id title techStack likes createdAt
    }
  }
`;
export const LIKE_SHOWCASE_PROJECT = gql`
  mutation LikeShowcaseProject($projectId: ID!) { likeShowcaseProject(projectId: $projectId) }
`;
export const DELETE_SHOWCASE_PROJECT = gql`
  mutation DeleteShowcaseProject($projectId: ID!) { deleteShowcaseProject(projectId: $projectId) }
`;

// ─── HACKATHONS ───────────────────────────────────────────────────────────────
export const HACKATHONS = gql`
  query Hackathons($status: String, $mode: String) {
    hackathons(status: $status, mode: $mode) {
      id title description organizer { id name college }
      startDate endDate registrationDeadline maxTeamSize minTeamSize
      themes prizes mode applyLink status college
      teams { id teamName leader { id name } memberCount skillsNeeded isOpen projectIdea members { user { id name skills } role } }
      createdAt
    }
  }
`;
export const MY_HACKATHON_TEAMS = gql`
  query MyHackathonTeams { myHackathonTeams { id teamName hackathonId leader { id name } memberCount members { user { id name } role } projectIdea skillsNeeded isOpen createdAt } }
`;
export const CREATE_HACKATHON = gql`
  mutation CreateHackathon($title: String!, $description: String!, $startDate: String, $endDate: String, $registrationDeadline: String, $maxTeamSize: Int, $minTeamSize: Int, $themes: [String!], $prizes: String, $mode: String, $applyLink: String, $college: String) {
    createHackathon(title: $title, description: $description, startDate: $startDate, endDate: $endDate, registrationDeadline: $registrationDeadline, maxTeamSize: $maxTeamSize, minTeamSize: $minTeamSize, themes: $themes, prizes: $prizes, mode: $mode, applyLink: $applyLink, college: $college) {
      id title status createdAt
    }
  }
`;
export const CREATE_HACKATHON_TEAM = gql`
  mutation CreateHackathonTeam($hackathonId: ID!, $teamName: String!, $projectIdea: String, $skillsNeeded: [String!], $role: String) {
    createHackathonTeam(hackathonId: $hackathonId, teamName: $teamName, projectIdea: $projectIdea, skillsNeeded: $skillsNeeded, role: $role) {
      id teamName memberCount
    }
  }
`;
export const JOIN_HACKATHON_TEAM = gql`
  mutation JoinHackathonTeam($teamId: ID!, $role: String) { joinHackathonTeam(teamId: $teamId, role: $role) }
`;
export const LEAVE_HACKATHON_TEAM = gql`
  mutation LeaveHackathonTeam($teamId: ID!) { leaveHackathonTeam(teamId: $teamId) }
`;

// ─── INITIATIVES ──────────────────────────────────────────────────────────────
export const INITIATIVES = gql`
  query Initiatives($category: String) {
    initiatives(category: $category) {
      id title description creator { id name college avatarUrl }
      category tags maxMembers isOpen college memberCount
      members { user { id name skills } role }
      createdAt
    }
  }
`;
export const MY_INITIATIVES = gql`
  query MyInitiatives { myInitiatives { id title description category memberCount isOpen createdAt } }
`;
export const CREATE_INITIATIVE = gql`
  mutation CreateInitiative($title: String!, $description: String!, $category: String, $tags: [String!], $maxMembers: Int, $college: String) {
    createInitiative(title: $title, description: $description, category: $category, tags: $tags, maxMembers: $maxMembers, college: $college) {
      id title category memberCount createdAt
    }
  }
`;
export const JOIN_INITIATIVE = gql`
  mutation JoinInitiative($initiativeId: ID!) { joinInitiative(initiativeId: $initiativeId) }
`;
export const LEAVE_INITIATIVE = gql`
  mutation LeaveInitiative($initiativeId: ID!) { leaveInitiative(initiativeId: $initiativeId) }
`;

// ─── NETWORK & FRIENDS ────────────────────────────────────────────────────────
export const MY_FRIENDS = gql`
  query MyFriends { myFriends { id friend { id name email college branch skills hobbies avatarUrl } status isRequester createdAt } }
`;
export const FRIEND_REQUESTS = gql`
  query FriendRequests { friendRequests { id friend { id name email college branch skills } isRequester createdAt } }
`;
export const SEARCH_USERS = gql`
  query SearchUsers($query: String!, $skill: String) {
    searchUsers(query: $query, skill: $skill) { id name email college branch skills hobbies avatarUrl }
  }
`;
export const SEND_FRIEND_REQUEST = gql`
  mutation SendFriendRequest($userId: ID!) { sendFriendRequest(userId: $userId) { id status } }
`;
export const RESPOND_FRIEND_REQUEST = gql`
  mutation RespondFriendRequest($friendshipId: ID!, $accept: Boolean!) { respondFriendRequest(friendshipId: $friendshipId, accept: $accept) { id status } }
`;
export const REMOVE_FRIEND = gql`
  mutation RemoveFriend($friendshipId: ID!) { removeFriend(friendshipId: $friendshipId) }
`;
export const MY_STUDY_GROUPS = gql`
  query MyStudyGroups { myStudyGroups { id name subject description creator { id name } memberCount members { id user { id name skills } chapterAssigned chapterStatus notesUrl } createdAt } }
`;
export const CREATE_STUDY_GROUP = gql`
  mutation CreateStudyGroup($name: String!, $subject: String!, $description: String, $memberIds: [ID!]) {
    createStudyGroup(name: $name, subject: $subject, description: $description, memberIds: $memberIds) {
      id name subject memberCount createdAt
    }
  }
`;
export const ASSIGN_CHAPTER = gql`
  mutation AssignChapter($groupId: ID!, $userId: ID!, $chapterAssigned: String!, $notesUrl: String) {
    assignChapter(groupId: $groupId, userId: $userId, chapterAssigned: $chapterAssigned, notesUrl: $notesUrl) {
      id chapterAssigned chapterStatus notesUrl
    }
  }
`;
export const UPDATE_CHAPTER_STATUS = gql`
  mutation UpdateChapterStatus($groupId: ID!, $status: String!) {
    updateChapterStatus(groupId: $groupId, status: $status) { id chapterStatus }
  }
`;
export const UPDATE_HOBBIES = gql`
  mutation UpdateHobbies($hobbies: [String!]!) { updateHobbies(hobbies: $hobbies) { id hobbies } }
`;

// ─── TECH SOCIAL FEED ─────────────────────────────────────────────────────────
export const SOCIAL_FEED = gql`
  query SocialFeed($postType: String, $tag: String) {
    socialFeed(postType: $postType, tag: $tag) {
      id postType title body url tags audience
      author { id name college avatarUrl skills }
      likes hasLiked
      comments { id body author { id name avatarUrl } createdAt }
      createdAt
    }
  }
`;
export const MY_SOCIAL_POSTS = gql`
  query MySocialPosts { mySocialPosts { id postType title body url tags likes hasLiked createdAt } }
`;
export const CREATE_SOCIAL_POST = gql`
  mutation CreateSocialPost($postType: String!, $title: String!, $body: String, $url: String, $tags: [String!], $audience: String) {
    createSocialPost(postType: $postType, title: $title, body: $body, url: $url, tags: $tags, audience: $audience) {
      id postType title audience likes createdAt
    }
  }
`;
export const LIKE_SOCIAL_POST = gql`
  mutation LikeSocialPost($postId: ID!) { likeSocialPost(postId: $postId) }
`;
export const COMMENT_ON_POST = gql`
  mutation CommentOnPost($postId: ID!, $body: String!) {
    commentOnPost(postId: $postId, body: $body) { id body author { id name } createdAt }
  }
`;
export const DELETE_SOCIAL_POST = gql`
  mutation DeleteSocialPost($postId: ID!) { deleteSocialPost(postId: $postId) }
`;

// ─── SHOPKEEPER MARKETPLACE ───────────────────────────────────────────────────
export const CREATE_SHOP_LISTING = gql`
  mutation CreateShopListing($title: String!, $description: String!, $price: Float!, $category: String!, $contactInfo: String!, $shopName: String!, $deliveryAvailable: Boolean) {
    createShopListing(title: $title, description: $description, price: $price, category: $category, contactInfo: $contactInfo, shopName: $shopName, deliveryAvailable: $deliveryAvailable) {
      id title price sellerType shopName deliveryAvailable createdAt
    }
  }
`;

// ─── MATCHER ─────────────────────────────────────────────────────────────────
export const MATCH_USERS = gql`
  query MatchUsers($mode: String, $lookingFor: [String!], $college: String, $city: String, $branch: String, $year: Int, $minScore: Int, $limit: Int) {
    matchUsers(mode: $mode, lookingFor: $lookingFor, college: $college, city: $city, branch: $branch, year: $year, minScore: $minScore, limit: $limit) {
      user {
        id name email college branch city year bio avatarUrl
        skills hobbies lookingFor openToConnect
      }
      score
      breakdown { skillScore profileScore locationScore hobbyScore lfScore }
      sharedSkills
      novelSkills
      sharedHobbies
    }
  }
`;

export const MY_MATCH_PROFILE = gql`
  query MyMatchProfile {
    myMatchProfile {
      id name email college branch city year bio skills hobbies lookingFor openToConnect matchVisibility
    }
  }
`;

export const UPDATE_MATCH_PROFILE = gql`
  mutation UpdateMatchProfile($city: String, $lookingFor: [String!], $openToConnect: Boolean, $matchVisibility: String) {
    updateMatchProfile(city: $city, lookingFor: $lookingFor, openToConnect: $openToConnect, matchVisibility: $matchVisibility) {
      id city lookingFor openToConnect matchVisibility
    }
  }
`;

export const UPDATE_STUDENT_PROFILE = gql`
  mutation UpdateStudentProfile($section: String, $enrolledSubjects: [String!]) {
    updateStudentProfile(section: $section, enrolledSubjects: $enrolledSubjects) {
      id section enrolledSubjects
    }
  }
`;

// ─── Company Portal ───────────────────────────────────────────────────────────
const CO_FIELDS = `id name email logoUrl website description companyType size location industry createdAt`;
const COMP_EVENT_FIELDS = `
  id title eventType description bannerUrl startDate endDate registrationDeadline
  venue meetingLink maxParticipants prizePool tags status registrations isRegistered createdAt
  company { ${CO_FIELDS} }
`;
const OPP_FIELDS = `
  id type title description location isRemote stipend ctc duration
  skills branches minCgpa deadline openings status applications hasApplied createdAt
  company { ${CO_FIELDS} }
`;

export const REGISTER_COMPANY = gql`
  mutation RegisterCompany($name: String!, $email: String!, $password: String!, $companyType: CompanyType, $website: String, $description: String, $logoUrl: String, $size: String, $location: String, $industry: String) {
    registerCompany(name: $name, email: $email, password: $password, companyType: $companyType, website: $website, description: $description, logoUrl: $logoUrl, size: $size, location: $location, industry: $industry) {
      token company { ${CO_FIELDS} }
    }
  }
`;

export const LOGIN_COMPANY = gql`
  mutation LoginCompany($email: String!, $password: String!) {
    loginCompany(email: $email, password: $password) {
      token company { ${CO_FIELDS} }
    }
  }
`;

export const COMPANY_EVENTS = gql`
  query CompanyEvents($eventType: CompanyEventType, $companyId: ID, $upcoming: Boolean, $search: String) {
    companyEvents(eventType: $eventType, companyId: $companyId, upcoming: $upcoming, search: $search) {
      ${COMP_EVENT_FIELDS}
    }
  }
`;

export const OPPORTUNITIES = gql`
  query Opportunities($type: OpportunityType, $skills: [String!], $branch: String, $remote: Boolean, $search: String) {
    opportunities(type: $type, skills: $skills, branch: $branch, remote: $remote, search: $search) {
      ${OPP_FIELDS}
    }
  }
`;

export const MY_OPPORTUNITY_APPLICATIONS = gql`
  query MyOpportunityApplications {
    myOpportunityApplications {
      id status coverNote appliedAt
      opportunity { ${OPP_FIELDS} }
    }
  }
`;

export const MY_COMPANY = gql`
  query MyCompany { myCompany { ${CO_FIELDS} } }
`;

export const MY_COMPANY_EVENTS = gql`
  query MyCompanyEvents { myCompanyEvents { ${COMP_EVENT_FIELDS} } }
`;

export const MY_OPPORTUNITIES = gql`
  query MyOpportunities { myOpportunities { ${OPP_FIELDS} } }
`;

export const OPPORTUNITY_APPLICANTS = gql`
  query OpportunityApplicants($opportunityId: ID!) {
    opportunityApplicants(opportunityId: $opportunityId) {
      id status coverNote appliedAt
      student { id name email college branch year section enrolledSubjects skills avatarUrl }
    }
  }
`;

export const COMPANY_EVENT_REGISTRANTS = gql`
  query CompanyEventRegistrants($eventId: ID!) {
    companyEventRegistrants(eventId: $eventId) {
      id name email college branch year section skills avatarUrl
    }
  }
`;

export const CREATE_COMPANY_EVENT = gql`
  mutation CreateCompanyEvent($title: String!, $eventType: CompanyEventType!, $description: String!, $bannerUrl: String, $startDate: String!, $endDate: String, $registrationDeadline: String, $venue: String, $meetingLink: String, $maxParticipants: Int, $prizePool: String, $tags: [String!]) {
    createCompanyEvent(title: $title, eventType: $eventType, description: $description, bannerUrl: $bannerUrl, startDate: $startDate, endDate: $endDate, registrationDeadline: $registrationDeadline, venue: $venue, meetingLink: $meetingLink, maxParticipants: $maxParticipants, prizePool: $prizePool, tags: $tags) {
      ${COMP_EVENT_FIELDS}
    }
  }
`;

export const REGISTER_FOR_COMPANY_EVENT = gql`
  mutation RegisterForCompanyEvent($eventId: ID!) { registerForCompanyEvent(eventId: $eventId) }
`;

export const CREATE_OPPORTUNITY = gql`
  mutation CreateOpportunity($type: OpportunityType!, $title: String!, $description: String!, $location: String, $isRemote: Boolean, $stipend: String, $ctc: String, $duration: String, $skills: [String!], $branches: [String!], $minCgpa: Float, $deadline: String, $openings: Int) {
    createOpportunity(type: $type, title: $title, description: $description, location: $location, isRemote: $isRemote, stipend: $stipend, ctc: $ctc, duration: $duration, skills: $skills, branches: $branches, minCgpa: $minCgpa, deadline: $deadline, openings: $openings) {
      ${OPP_FIELDS}
    }
  }
`;

export const APPLY_TO_OPPORTUNITY = gql`
  mutation ApplyToOpportunity($opportunityId: ID!, $coverNote: String) {
    applyToOpportunity(opportunityId: $opportunityId, coverNote: $coverNote) {
      id status appliedAt
    }
  }
`;

export const UPDATE_OPP_APP_STATUS = gql`
  mutation UpdateOpportunityAppStatus($applicationId: ID!, $status: OppAppStatus!) {
    updateOpportunityAppStatus(applicationId: $applicationId, status: $status) {
      id status
    }
  }
`;

export const CLOSE_OPPORTUNITY = gql`
  mutation CloseOpportunity($opportunityId: ID!) {
    closeOpportunity(opportunityId: $opportunityId) { id status }
  }
`;

// ─── College Admin ────────────────────────────────────────────────────────────
const ADMIN_FIELDS = `id name email college branch role designation avatarUrl approved createdAt`;
const NEWS_FIELDS  = `id title body imageUrl category college branch pinned views tags createdAt admin { ${ADMIN_FIELDS} }`;
const ANN_FIELDS   = `id title body type subject pinned createdAt college branch views author { id name college branch }`;

export const REGISTER_COLLEGE_ADMIN = gql`
  mutation RegisterCollegeAdmin($name: String!, $email: String!, $password: String!, $college: String!, $branch: String, $role: AdminRole, $designation: String, $avatarUrl: String) {
    registerCollegeAdmin(name: $name, email: $email, password: $password, college: $college, branch: $branch, role: $role, designation: $designation, avatarUrl: $avatarUrl) {
      token admin { ${ADMIN_FIELDS} }
    }
  }
`;

export const LOGIN_COLLEGE_ADMIN = gql`
  mutation LoginCollegeAdmin($email: String!, $password: String!) {
    loginCollegeAdmin(email: $email, password: $password) {
      token admin { ${ADMIN_FIELDS} }
    }
  }
`;

export const MY_ADMIN_PROFILE = gql`
  query MyAdminProfile { myAdminProfile { ${ADMIN_FIELDS} } }
`;

export const CAMPUS_NEWS = gql`
  query CampusNews($college: String, $branch: String, $category: NewsCategory, $search: String, $pinned: Boolean) {
    campusNews(college: $college, branch: $branch, category: $category, search: $search, pinned: $pinned) {
      ${NEWS_FIELDS}
    }
  }
`;

export const MY_CAMPUS_NEWS = gql`
  query MyCampusNews { myCampusNews { ${NEWS_FIELDS} } }
`;

export const ADMIN_ANNOUNCEMENTS = gql`
  query AdminAnnouncements { adminAnnouncements { ${ANN_FIELDS} } }
`;

export const POST_CAMPUS_NEWS = gql`
  mutation PostCampusNews($title: String!, $body: String!, $category: NewsCategory, $imageUrl: String, $tags: [String!], $pinned: Boolean) {
    postCampusNews(title: $title, body: $body, category: $category, imageUrl: $imageUrl, tags: $tags, pinned: $pinned) {
      ${NEWS_FIELDS}
    }
  }
`;

export const UPDATE_CAMPUS_NEWS = gql`
  mutation UpdateCampusNews($newsId: ID!, $title: String, $body: String, $category: NewsCategory, $imageUrl: String, $tags: [String!], $pinned: Boolean) {
    updateCampusNews(newsId: $newsId, title: $title, body: $body, category: $category, imageUrl: $imageUrl, tags: $tags, pinned: $pinned) {
      ${NEWS_FIELDS}
    }
  }
`;

export const DELETE_CAMPUS_NEWS = gql`
  mutation DeleteCampusNews($newsId: ID!) { deleteCampusNews(newsId: $newsId) }
`;

export const PIN_CAMPUS_NEWS = gql`
  mutation PinCampusNews($newsId: ID!, $pinned: Boolean!) {
    pinCampusNews(newsId: $newsId, pinned: $pinned) { id pinned }
  }
`;

export const POST_ADMIN_ANNOUNCEMENT = gql`
  mutation PostAdminAnnouncement($title: String!, $body: String!, $type: AnnouncementType!, $subject: String, $pinned: Boolean) {
    postAdminAnnouncement(title: $title, body: $body, type: $type, subject: $subject, pinned: $pinned) {
      ${ANN_FIELDS}
    }
  }
`;

export const DELETE_ADMIN_ANNOUNCEMENT = gql`
  mutation DeleteAdminAnnouncement($id: ID!) { deleteAdminAnnouncement(id: $id) }
`;

export const UPDATE_ADMIN_PROFILE = gql`
  mutation UpdateAdminProfile($name: String, $designation: String, $avatarUrl: String) {
    updateAdminProfile(name: $name, designation: $designation, avatarUrl: $avatarUrl) {
      ${ADMIN_FIELDS}
    }
  }
`;

// ─── SUPER ADMIN ─────────────────────────────────────────────────────────────
const SUPER_ADMIN_FIELDS = `id name email avatarUrl phone createdAt`;

export const REGISTER_SUPER_ADMIN = gql`
  mutation RegisterSuperAdmin($name: String!, $email: String!, $password: String!, $phone: String) {
    registerSuperAdmin(name: $name, email: $email, password: $password, phone: $phone) {
      token
      superAdmin { ${SUPER_ADMIN_FIELDS} }
    }
  }
`;

export const LOGIN_SUPER_ADMIN = gql`
  mutation LoginSuperAdmin($email: String!, $password: String!) {
    loginSuperAdmin(email: $email, password: $password) {
      token
      superAdmin { ${SUPER_ADMIN_FIELDS} }
    }
  }
`;

export const MY_SUPER_ADMIN_PROFILE = gql`
  query MySuperAdminProfile { mySuperAdminProfile { ${SUPER_ADMIN_FIELDS} } }
`;

export const PENDING_ADMIN_REQUESTS = gql`
  query PendingAdminRequests {
    pendingAdminRequests {
      id name email college branch role designation phone approved rejectionNote createdAt
    }
  }
`;

export const ALL_COLLEGE_ADMINS = gql`
  query AllCollegeAdmins($college: String) {
    allCollegeAdmins(college: $college) {
      id name email college branch role designation phone approved rejectionNote createdAt
    }
  }
`;

export const APPROVE_COLLEGE_ADMIN = gql`
  mutation ApproveCollegeAdmin($adminId: ID!, $approved: Boolean!, $rejectionNote: String) {
    approveCollegeAdmin(adminId: $adminId, approved: $approved, rejectionNote: $rejectionNote) {
      id name email approved rejectionNote
    }
  }
`;

// ─── TEACHER ─────────────────────────────────────────────────────────────────
const TEACHER_FIELDS = `id name email college branch department designation subjects avatarUrl employeeId phone approved createdAt`;

export const REGISTER_TEACHER = gql`
  mutation RegisterTeacher($name: String!, $email: String!, $password: String!, $college: String!, $branch: String, $department: String, $designation: String, $subjects: [String!], $employeeId: String, $phone: String) {
    registerTeacher(name: $name, email: $email, password: $password, college: $college, branch: $branch, department: $department, designation: $designation, subjects: $subjects, employeeId: $employeeId, phone: $phone) {
      token
      teacher { ${TEACHER_FIELDS} }
    }
  }
`;

export const LOGIN_TEACHER = gql`
  mutation LoginTeacher($email: String!, $password: String!) {
    loginTeacher(email: $email, password: $password) {
      token
      teacher { ${TEACHER_FIELDS} }
    }
  }
`;

export const MY_TEACHER_PROFILE = gql`
  query MyTeacherProfile { myTeacherProfile { ${TEACHER_FIELDS} } }
`;

export const UPDATE_TEACHER_PROFILE = gql`
  mutation UpdateTeacherProfile($name: String, $designation: String, $subjects: [String!], $avatarUrl: String) {
    updateTeacherProfile(name: $name, designation: $designation, subjects: $subjects, avatarUrl: $avatarUrl) {
      ${TEACHER_FIELDS}
    }
  }
`;

export const POST_TEACHER_ANNOUNCEMENT = gql`
  mutation PostTeacherAnnouncement($title: String!, $body: String!, $type: AnnouncementType!, $subject: String, $pinned: Boolean) {
    postTeacherAnnouncement(title: $title, body: $body, type: $type, subject: $subject, pinned: $pinned) {
      id title body type subject pinned createdAt
    }
  }
`;

export const TEACHER_ANNOUNCEMENTS = gql`
  query TeacherAnnouncements {
    teacherAnnouncements { id title body type subject pinned createdAt }
  }
`;

// ─── FOOD SHARING ─────────────────────────────────────────────────────────────
const FOOD_ORDER_FIELDS = `
  id restaurant restaurantUrl deliveryAddress cutoffTime maxPeople notes status college hostel createdAt memberCount isMember
  creator { id name email avatarUrl }
  members { id items amount paid user { id name email avatarUrl } }
`;

export const FOOD_ORDERS = gql`
  query FoodOrders($college: String, $status: String) {
    foodOrders(college: $college, status: $status) { ${FOOD_ORDER_FIELDS} }
  }
`;

export const MY_FOOD_ORDERS = gql`
  query MyFoodOrders { myFoodOrders { ${FOOD_ORDER_FIELDS} } }
`;

export const CREATE_FOOD_ORDER = gql`
  mutation CreateFoodOrder($restaurant: String!, $deliveryAddress: String!, $cutoffTime: String!, $restaurantUrl: String, $maxPeople: Int, $notes: String, $hostel: String) {
    createFoodOrder(restaurant: $restaurant, deliveryAddress: $deliveryAddress, cutoffTime: $cutoffTime, restaurantUrl: $restaurantUrl, maxPeople: $maxPeople, notes: $notes, hostel: $hostel) {
      ${FOOD_ORDER_FIELDS}
    }
  }
`;

export const JOIN_FOOD_ORDER = gql`
  mutation JoinFoodOrder($orderId: ID!, $items: String, $amount: Float) {
    joinFoodOrder(orderId: $orderId, items: $items, amount: $amount)
  }
`;

export const LEAVE_FOOD_ORDER = gql`
  mutation LeaveFoodOrder($orderId: ID!) { leaveFoodOrder(orderId: $orderId) }
`;

export const UPDATE_FOOD_ORDER_STATUS = gql`
  mutation UpdateFoodOrderStatus($orderId: ID!, $status: String!) {
    updateFoodOrderStatus(orderId: $orderId, status: $status) { id status }
  }
`;

export const UPDATE_MY_FOOD_ITEMS = gql`
  mutation UpdateMyFoodItems($orderId: ID!, $items: String, $amount: Float) {
    updateMyFoodItems(orderId: $orderId, items: $items, amount: $amount)
  }
`;

// ─── CAB SHARING ──────────────────────────────────────────────────────────────
const CAB_SHARE_FIELDS = `
  id fromLocation toLocation departureTime availableSeats farePerPerson notes contact status college createdAt memberCount isMember
  creator { id name email avatarUrl }
  members { id user { id name email avatarUrl } }
`;

export const CAB_SHARES = gql`
  query CabShares($college: String, $status: String) {
    cabShares(college: $college, status: $status) { ${CAB_SHARE_FIELDS} }
  }
`;

export const MY_CAB_SHARES = gql`
  query MyCabShares { myCabShares { ${CAB_SHARE_FIELDS} } }
`;

export const CREATE_CAB_SHARE = gql`
  mutation CreateCabShare($fromLocation: String!, $toLocation: String!, $departureTime: String!, $availableSeats: Int, $farePerPerson: Float, $notes: String, $contact: String) {
    createCabShare(fromLocation: $fromLocation, toLocation: $toLocation, departureTime: $departureTime, availableSeats: $availableSeats, farePerPerson: $farePerPerson, notes: $notes, contact: $contact) {
      ${CAB_SHARE_FIELDS}
    }
  }
`;

export const JOIN_CAB_SHARE = gql`
  mutation JoinCabShare($cabId: ID!) { joinCabShare(cabId: $cabId) }
`;

export const LEAVE_CAB_SHARE = gql`
  mutation LeaveCabShare($cabId: ID!) { leaveCabShare(cabId: $cabId) }
`;

export const UPDATE_CAB_STATUS = gql`
  mutation UpdateCabStatus($cabId: ID!, $status: String!) {
    updateCabStatus(cabId: $cabId, status: $status) { id status }
  }
`;

// ─── WHATSAPP ─────────────────────────────────────────────────────────────────
export const UPDATE_PHONE = gql`
  mutation UpdatePhone($phone: String!, $whatsappOptIn: Boolean) {
    updatePhone(phone: $phone, whatsappOptIn: $whatsappOptIn) { id phone }
  }
`;

export const SEND_TEST_WHATSAPP = gql`
  mutation SendTestWhatsapp($phone: String!) { sendTestWhatsapp(phone: $phone) }
`;

// ─── UNIVERSITY / COLLEGE PROFILES ───────────────────────────────────────────
const UNI_FIELDS = `id name email logoUrl coverUrl website location city state established type description facilities accreditations naacGrade nirfRank totalStudents totalFaculty phone approved createdAt`;

export const REGISTER_UNIVERSITY = gql`
  mutation RegisterUniversity($name: String!, $email: String!, $password: String!, $website: String, $location: String, $city: String, $state: String, $established: Int, $type: String, $description: String, $phone: String) {
    registerUniversity(name: $name, email: $email, password: $password, website: $website, location: $location, city: $city, state: $state, established: $established, type: $type, description: $description, phone: $phone) {
      token
      university { ${UNI_FIELDS} }
    }
  }
`;

export const LOGIN_UNIVERSITY = gql`
  mutation LoginUniversity($email: String!, $password: String!) {
    loginUniversity(email: $email, password: $password) {
      token
      university { ${UNI_FIELDS} }
    }
  }
`;

export const MY_UNIVERSITY_PROFILE = gql`
  query MyUniversityProfile { myUniversityProfile { ${UNI_FIELDS} } }
`;

export const UNIVERSITIES = gql`
  query Universities($search: String, $type: String, $city: String) {
    universities(search: $search, type: $type, city: $city) { ${UNI_FIELDS} }
  }
`;

export const UPSERT_COLLEGE_PROFILE = gql`
  mutation UpsertCollegeProfile($logoUrl: String, $coverUrl: String, $about: String, $facilities: [String!], $accreditations: [String!], $naacGrade: String, $nirfRank: Int, $placementStats: String, $gallery: [String!], $website: String, $phone: String, $address: String) {
    upsertCollegeProfile(logoUrl: $logoUrl, coverUrl: $coverUrl, about: $about, facilities: $facilities, accreditations: $accreditations, naacGrade: $naacGrade, nirfRank: $nirfRank, placementStats: $placementStats, gallery: $gallery, website: $website, phone: $phone, address: $address) {
      id college logoUrl coverUrl about facilities accreditations naacGrade nirfRank placementStats gallery website phone address updatedAt
    }
  }
`;

export const COLLEGE_PROFILE = gql`
  query CollegeProfile($college: String!) {
    collegeProfile(college: $college) { id college logoUrl coverUrl about facilities accreditations naacGrade nirfRank placementStats gallery website phone address updatedAt }
  }
`;

// ─── MENTORS ──────────────────────────────────────────────────────────────────
const MENTOR_FIELDS = `id name email avatarUrl headline bio currentCompany currentRole collegeAttended graduationYear experienceYears expertise industries languages linkedinUrl availability sessionType rating reviewCount totalSessions isApproved isPreCollege hasSessioned reviews { id rating review reviewerName reviewerAvatar createdAt } createdAt`;

export const MENTORS = gql`
  query Mentors($expertise: String, $industry: String, $isPreCollege: Boolean, $search: String) {
    mentors(expertise: $expertise, industry: $industry, isPreCollege: $isPreCollege, search: $search) { ${MENTOR_FIELDS} }
  }
`;

export const MENTOR = gql`
  query Mentor($id: ID!) { mentor(id: $id) { ${MENTOR_FIELDS} } }
`;

export const APPLY_AS_MENTOR = gql`
  mutation ApplyAsMentor($name: String!, $email: String!, $password: String, $headline: String!, $bio: String!, $currentCompany: String, $currentRole: String, $collegeAttended: String, $graduationYear: Int, $experienceYears: Int, $expertise: [String!]!, $industries: [String!], $languages: [String!], $linkedinUrl: String, $availability: String, $sessionType: [String!], $isPreCollege: Boolean, $phone: String) {
    applyAsMentor(name: $name, email: $email, password: $password, headline: $headline, bio: $bio, currentCompany: $currentCompany, currentRole: $currentRole, collegeAttended: $collegeAttended, graduationYear: $graduationYear, experienceYears: $experienceYears, expertise: $expertise, industries: $industries, languages: $languages, linkedinUrl: $linkedinUrl, availability: $availability, sessionType: $sessionType, isPreCollege: $isPreCollege, phone: $phone) {
      id name headline isApproved
    }
  }
`;

export const LOGIN_MENTOR = gql`
  mutation LoginMentor($email: String!, $password: String!) {
    loginMentor(email: $email, password: $password) { token mentor { ${MENTOR_FIELDS} } }
  }
`;

export const APPROVE_MENTOR = gql`
  mutation ApproveMentor($mentorId: ID!, $approved: Boolean!) {
    approveMentor(mentorId: $mentorId, approved: $approved) { id name isApproved }
  }
`;

export const REQUEST_MENTOR_SESSION = gql`
  mutation RequestMentorSession($mentorId: ID!, $topic: String!, $message: String, $sessionType: String, $preferredTime: String) {
    requestMentorSession(mentorId: $mentorId, topic: $topic, message: $message, sessionType: $sessionType, preferredTime: $preferredTime) {
      id topic status scheduledAt meetingLink preferredTime createdAt
      mentor { id name headline avatarUrl }
      student { id name email }
    }
  }
`;

export const MY_MENTOR_SESSIONS = gql`
  query MyMentorSessions {
    myMentorSessions {
      id topic message sessionType status scheduledAt meetingLink preferredTime createdAt
      mentor { id name headline avatarUrl currentCompany }
      student { id name email avatarUrl }
    }
  }
`;

export const RESPOND_TO_MENTOR_SESSION = gql`
  mutation RespondToMentorSession($sessionId: ID!, $accept: Boolean!, $scheduledAt: String, $meetingLink: String, $notes: String) {
    respondToMentorSession(sessionId: $sessionId, accept: $accept, scheduledAt: $scheduledAt, meetingLink: $meetingLink, notes: $notes) {
      id status scheduledAt meetingLink notes
    }
  }
`;

export const REVIEW_MENTOR = gql`
  mutation ReviewMentor($mentorId: ID!, $sessionId: ID, $rating: Int!, $review: String) {
    reviewMentor(mentorId: $mentorId, sessionId: $sessionId, rating: $rating, review: $review)
  }
`;

// ─── EAC SEATS ────────────────────────────────────────────────────────────────
const EAC_SEAT_FIELDS = `id title description department program seatsAvailable seatsFilled requirements eligibilityCriteria benefits deadline college universityName contactEmail contactPhone category status isPreCollege applicationCount hasApplied createdAt`;

export const EAC_SEATS = gql`
  query EacSeats($category: String, $universityName: String, $search: String, $isPreCollege: Boolean) {
    eacSeats(category: $category, universityName: $universityName, search: $search, isPreCollege: $isPreCollege) { ${EAC_SEAT_FIELDS} }
  }
`;

export const EAC_SEAT = gql`
  query EacSeat($id: ID!) { eacSeat(id: $id) { ${EAC_SEAT_FIELDS} } }
`;

export const POST_EAC_SEAT = gql`
  mutation PostEacSeat($title: String!, $description: String, $department: String, $program: String, $seatsAvailable: Int!, $requirements: String, $eligibilityCriteria: String, $benefits: String, $deadline: String!, $universityName: String!, $contactEmail: String, $contactPhone: String, $category: String, $isPreCollege: Boolean) {
    postEacSeat(title: $title, description: $description, department: $department, program: $program, seatsAvailable: $seatsAvailable, requirements: $requirements, eligibilityCriteria: $eligibilityCriteria, benefits: $benefits, deadline: $deadline, universityName: $universityName, contactEmail: $contactEmail, contactPhone: $contactPhone, category: $category, isPreCollege: $isPreCollege) {
      ${EAC_SEAT_FIELDS}
    }
  }
`;

export const APPLY_FOR_EAC_SEAT = gql`
  mutation ApplyForEacSeat($seatId: ID!, $personalStatement: String) {
    applyForEacSeat(seatId: $seatId, personalStatement: $personalStatement) {
      id status appliedAt
      seat { id title universityName }
    }
  }
`;

export const MY_EAC_APPLICATIONS = gql`
  query MyEacApplications {
    myEacApplications {
      id status personalStatement adminNote appliedAt reviewedAt
      seat { ${EAC_SEAT_FIELDS} }
      applicant { id name email college branch year }
    }
  }
`;

export const REVIEW_EAC_APPLICATION = gql`
  mutation ReviewEacApplication($applicationId: ID!, $status: String!, $adminNote: String) {
    reviewEacApplication(applicationId: $applicationId, status: $status, adminNote: $adminNote) {
      id status adminNote reviewedAt
      applicant { id name email college }
    }
  }
`;

export const ADMIN_EAC_APPLICATIONS = gql`
  query AdminEacApplications($seatId: ID) {
    adminEacApplications(seatId: $seatId) {
      id status personalStatement adminNote appliedAt reviewedAt
      seat { id title universityName }
      applicant { id name email college branch year bio skills }
    }
  }
`;

// ─── FUTURE ASPIRATIONS ───────────────────────────────────────────────────────
export const MY_ASPIRATION = gql`
  query MyAspiration {
    myAspiration {
      id primaryGoal targetYear jobRoles jobCompanies jobLocations
      pgCountry pgUniversities pgPrograms pgExams
      researchFields researchLabs startupIdea govtExams notes updatedAt
    }
  }
`;

export const SAVE_ASPIRATION = gql`
  mutation SaveAspiration($primaryGoal: String!, $targetYear: Int, $jobRoles: [String!], $jobCompanies: [String!], $jobLocations: [String!], $pgCountry: String, $pgUniversities: [String!], $pgPrograms: [String!], $pgExams: [String!], $researchFields: [String!], $researchLabs: [String!], $startupIdea: String, $govtExams: [String!], $notes: String) {
    saveAspiration(primaryGoal: $primaryGoal, targetYear: $targetYear, jobRoles: $jobRoles, jobCompanies: $jobCompanies, jobLocations: $jobLocations, pgCountry: $pgCountry, pgUniversities: $pgUniversities, pgPrograms: $pgPrograms, pgExams: $pgExams, researchFields: $researchFields, researchLabs: $researchLabs, startupIdea: $startupIdea, govtExams: $govtExams, notes: $notes) {
      id primaryGoal targetYear updatedAt
    }
  }
`;

export const MY_ASPIRATION_EXAMS = gql`
  query MyAspirationExams { myAspirationExams { id examName targetScore actualScore examDate registrationDeadline status notes createdAt } }
`;

export const UPSERT_ASPIRATION_EXAM = gql`
  mutation UpsertAspirationExam($examName: String!, $targetScore: String, $examDate: String, $registrationDeadline: String, $status: String, $notes: String) {
    upsertAspirationExam(examName: $examName, targetScore: $targetScore, examDate: $examDate, registrationDeadline: $registrationDeadline, status: $status, notes: $notes) {
      id examName targetScore actualScore examDate registrationDeadline status notes
    }
  }
`;

export const DELETE_ASPIRATION_EXAM = gql`
  mutation DeleteAspirationExam($examId: ID!) { deleteAspirationExam(examId: $examId) }
`;

export const MY_ASPIRATION_APPS = gql`
  query MyAspirationApps { myAspirationApps { id aspirationType institution program country deadline status notes documentsChecklist createdAt } }
`;

export const ADD_ASPIRATION_APP = gql`
  mutation AddAspirationApp($aspirationType: String!, $institution: String!, $program: String, $country: String, $deadline: String, $notes: String, $documentsChecklist: [String!]) {
    addAspirationApp(aspirationType: $aspirationType, institution: $institution, program: $program, country: $country, deadline: $deadline, notes: $notes, documentsChecklist: $documentsChecklist) {
      id aspirationType institution program country deadline status documentsChecklist
    }
  }
`;

export const UPDATE_ASPIRATION_APP = gql`
  mutation UpdateAspirationApp($appId: ID!, $status: String, $notes: String, $documentsChecklist: [String!]) {
    updateAspirationApp(appId: $appId, status: $status, notes: $notes, documentsChecklist: $documentsChecklist) {
      id status notes documentsChecklist
    }
  }
`;

export const DELETE_ASPIRATION_APP = gql`
  mutation DeleteAspirationApp($appId: ID!) { deleteAspirationApp(appId: $appId) }
`;

export const ASPIRATION_RESOURCES = gql`
  query AspirationResources($goal: String!, $country: String) {
    aspirationResources(goal: $goal, country: $country) { category title url description tags }
  }
`;

// ─── PERSONAL FINANCE ─────────────────────────────────────────────────────────
export const MY_FINANCE_ACCOUNTS = gql`
  query MyFinanceAccounts { myFinanceAccounts { id name accountType balance color icon isDefault createdAt } }
`;

export const ADD_FINANCE_ACCOUNT = gql`
  mutation AddFinanceAccount($name: String!, $accountType: String!, $balance: Float, $color: String, $icon: String, $isDefault: Boolean) {
    addFinanceAccount(name: $name, accountType: $accountType, balance: $balance, color: $color, icon: $icon, isDefault: $isDefault) {
      id name accountType balance color icon isDefault
    }
  }
`;

export const UPDATE_FINANCE_ACCOUNT = gql`
  mutation UpdateFinanceAccount($accountId: ID!, $name: String, $balance: Float, $color: String) {
    updateFinanceAccount(accountId: $accountId, name: $name, balance: $balance, color: $color) {
      id name balance color
    }
  }
`;

export const DELETE_FINANCE_ACCOUNT = gql`
  mutation DeleteFinanceAccount($accountId: ID!) { deleteFinanceAccount(accountId: $accountId) }
`;

export const MY_TRANSACTIONS = gql`
  query MyTransactions($month: Int, $year: Int, $category: String, $type: String) {
    myTransactions(month: $month, year: $year, category: $category, type: $type) {
      id amount type category description accountId tags date reference createdAt
    }
  }
`;

export const ADD_TRANSACTION = gql`
  mutation AddTransaction($amount: Float!, $type: String!, $category: String!, $description: String, $accountId: ID, $tags: [String!], $date: String, $reference: String) {
    addTransaction(amount: $amount, type: $type, category: $category, description: $description, accountId: $accountId, tags: $tags, date: $date, reference: $reference) {
      id amount type category description date tags
    }
  }
`;

export const DELETE_TRANSACTION = gql`
  mutation DeleteTransaction($txId: ID!) { deleteTransaction(txId: $txId) }
`;

export const MY_BUDGETS = gql`
  query MyBudgets($month: Int!, $year: Int!) {
    myBudgets(month: $month, year: $year) { id category budgetedAmount spentAmount month year }
  }
`;

export const SET_BUDGET = gql`
  mutation SetBudget($month: Int!, $year: Int!, $category: String!, $budgetedAmount: Float!) {
    setBudget(month: $month, year: $year, category: $category, budgetedAmount: $budgetedAmount) {
      id category budgetedAmount spentAmount
    }
  }
`;

export const MY_FINANCE_GOALS = gql`
  query MyFinanceGoals { myFinanceGoals { id title targetAmount savedAmount deadline category color progress createdAt } }
`;

export const ADD_FINANCE_GOAL = gql`
  mutation AddFinanceGoal($title: String!, $targetAmount: Float!, $deadline: String, $category: String, $color: String) {
    addFinanceGoal(title: $title, targetAmount: $targetAmount, deadline: $deadline, category: $category, color: $color) {
      id title targetAmount savedAmount deadline category color progress
    }
  }
`;

export const UPDATE_FINANCE_GOAL = gql`
  mutation UpdateFinanceGoal($goalId: ID!, $savedAmount: Float, $title: String, $targetAmount: Float) {
    updateFinanceGoal(goalId: $goalId, savedAmount: $savedAmount, title: $title, targetAmount: $targetAmount) {
      id title targetAmount savedAmount progress
    }
  }
`;

export const DELETE_FINANCE_GOAL = gql`
  mutation DeleteFinanceGoal($goalId: ID!) { deleteFinanceGoal(goalId: $goalId) }
`;

export const MY_FINANCE_SUMMARY = gql`
  query MyFinanceSummary($month: Int!, $year: Int!) {
    myFinanceSummary(month: $month, year: $year) {
      month year totalIncome totalExpenses netSavings savingsRate
      expenseByCategory { category amount percentage count }
      incomeByCategory  { category amount percentage count }
      budgetStatus      { id category budgetedAmount spentAmount }
      topExpenses       { id amount category description date }
    }
  }
`;

// ─── GIG PROJECTS ─────────────────────────────────────────────────────────────
const GIG_FIELDS = `id title description skillsRequired budgetMin budgetMax duration projectType category deliverables requirements status deadline college isRemote maxApplicants applicationCount hasApplied createdAt poster { id name email avatarUrl college }`;

export const GIG_PROJECTS = gql`
  query GigProjects($category: String, $skill: String, $search: String, $projectType: String, $college: String) {
    gigProjects(category: $category, skill: $skill, search: $search, projectType: $projectType, college: $college) { ${GIG_FIELDS} }
  }
`;

export const MY_GIG_PROJECTS = gql`
  query MyGigProjects { myGigProjects { ${GIG_FIELDS} } }
`;

export const POST_GIG_PROJECT = gql`
  mutation PostGigProject($title: String!, $description: String!, $skillsRequired: [String!]!, $budgetMin: Float, $budgetMax: Float, $duration: String, $projectType: String, $category: String, $deliverables: [String!], $requirements: String, $deadline: String, $isRemote: Boolean, $maxApplicants: Int) {
    postGigProject(title: $title, description: $description, skillsRequired: $skillsRequired, budgetMin: $budgetMin, budgetMax: $budgetMax, duration: $duration, projectType: $projectType, category: $category, deliverables: $deliverables, requirements: $requirements, deadline: $deadline, isRemote: $isRemote, maxApplicants: $maxApplicants) {
      ${GIG_FIELDS}
    }
  }
`;

export const UPDATE_GIG_PROJECT = gql`
  mutation UpdateGigProject($projectId: ID!, $status: String, $title: String, $description: String) {
    updateGigProject(projectId: $projectId, status: $status, title: $title, description: $description) { id status title }
  }
`;

export const DELETE_GIG_PROJECT = gql`
  mutation DeleteGigProject($projectId: ID!) { deleteGigProject(projectId: $projectId) }
`;

export const APPLY_FOR_GIG = gql`
  mutation ApplyForGig($projectId: ID!, $proposal: String!, $expectedAmount: Float, $portfolioUrl: String) {
    applyForGig(projectId: $projectId, proposal: $proposal, expectedAmount: $expectedAmount, portfolioUrl: $portfolioUrl) {
      id status proposal expectedAmount appliedAt
      project { id title }
      applicant { id name email }
    }
  }
`;

export const MY_GIG_APPLICATIONS = gql`
  query MyGigApplications {
    myGigApplications {
      id status proposal expectedAmount portfolioUrl appliedAt
      project { ${GIG_FIELDS} }
      applicant { id name email avatarUrl }
    }
  }
`;

export const GIG_APPLICANTS = gql`
  query GigApplicants($projectId: ID!) {
    gigApplicants(projectId: $projectId) {
      id status proposal expectedAmount portfolioUrl appliedAt
      applicant { id name email avatarUrl college branch skills bio }
    }
  }
`;

export const RESPOND_TO_GIG_APPLICATION = gql`
  mutation RespondToGigApplication($applicationId: ID!, $accept: Boolean!) {
    respondToGigApplication(applicationId: $applicationId, accept: $accept) {
      id status
      applicant { id name email }
    }
  }
`;

export const CREATE_GIG_CONTRACT = gql`
  mutation CreateGigContract($projectId: ID!, $freelancerId: ID!, $agreedAmount: Float!, $startDate: String, $endDate: String, $deliverables: [String!]) {
    createGigContract(projectId: $projectId, freelancerId: $freelancerId, agreedAmount: $agreedAmount, startDate: $startDate, endDate: $endDate, deliverables: $deliverables) {
      id agreedAmount status startDate endDate deliverables
      project { id title }
      client { id name }
      freelancer { id name }
    }
  }
`;

export const MY_GIG_CONTRACTS = gql`
  query MyGigContracts {
    myGigContracts {
      id agreedAmount status startDate endDate deliverables notes createdAt
      project { id title category }
      client { id name email avatarUrl }
      freelancer { id name email avatarUrl }
    }
  }
`;

// ─── STUDY MATERIALS ─────────────────────────────────────────────────────────
const MAT_FIELDS = `id title description subject semester branch college materialType fileUrl thumbnailUrl sourceUrl tags downloads likes views isCurated isApproved language pages hasLiked hasSaved createdAt uploader { id name avatarUrl college }`;

export const STUDY_MATERIALS = gql`
  query StudyMaterials($subject: String, $semester: Int, $branch: String, $college: String, $materialType: MaterialType, $search: String, $tag: String, $curated: Boolean, $savedOnly: Boolean, $language: String) {
    studyMaterials(subject: $subject, semester: $semester, branch: $branch, college: $college, materialType: $materialType, search: $search, tag: $tag, curated: $curated, savedOnly: $savedOnly, language: $language) { ${MAT_FIELDS} }
  }
`;

export const STUDY_MATERIAL = gql`
  query StudyMaterial($id: ID!) { studyMaterial(id: $id) { ${MAT_FIELDS} } }
`;

export const MY_SAVED_MATERIALS = gql`
  query MySavedMaterials { mySavedMaterials { ${MAT_FIELDS} } }
`;

export const MY_UPLOADED_MATERIALS = gql`
  query MyUploadedMaterials { myUploadedMaterials { ${MAT_FIELDS} } }
`;

export const UPLOAD_STUDY_MATERIAL = gql`
  mutation UploadStudyMaterial($title: String!, $description: String, $subject: String!, $semester: Int, $branch: String, $college: String, $materialType: MaterialType!, $fileUrl: String, $thumbnailUrl: String, $sourceUrl: String, $tags: [String!], $language: String, $pages: Int) {
    uploadStudyMaterial(title: $title, description: $description, subject: $subject, semester: $semester, branch: $branch, college: $college, materialType: $materialType, fileUrl: $fileUrl, thumbnailUrl: $thumbnailUrl, sourceUrl: $sourceUrl, tags: $tags, language: $language, pages: $pages) {
      ${MAT_FIELDS}
    }
  }
`;

export const LIKE_STUDY_MATERIAL = gql`
  mutation LikeStudyMaterial($materialId: ID!) { likeStudyMaterial(materialId: $materialId) }
`;

export const SAVE_STUDY_MATERIAL = gql`
  mutation SaveStudyMaterial($materialId: ID!) { saveStudyMaterial(materialId: $materialId) }
`;

export const INCREMENT_MATERIAL_DOWNLOAD = gql`
  mutation IncrementMaterialDownload($materialId: ID!) { incrementMaterialDownload(materialId: $materialId) }
`;

export const DELETE_STUDY_MATERIAL = gql`
  mutation DeleteStudyMaterial($materialId: ID!) { deleteStudyMaterial(materialId: $materialId) }
`;

export const MATERIAL_REQUESTS = gql`
  query MaterialRequests($subject: String, $status: MaterialRequestStatus) {
    materialRequests(subject: $subject, status: $status) {
      id subject semester branch college description materialType status fulfilledBy createdAt
      requester { id name college branch avatarUrl }
    }
  }
`;

export const MY_MATERIAL_REQUESTS = gql`
  query MyMaterialRequests {
    myMaterialRequests {
      id subject semester description materialType status fulfilledBy createdAt
      requester { id name }
    }
  }
`;

export const REQUEST_MATERIAL = gql`
  mutation RequestMaterial($subject: String!, $semester: Int, $branch: String, $college: String, $description: String!, $materialType: String) {
    requestMaterial(subject: $subject, semester: $semester, branch: $branch, college: $college, description: $description, materialType: $materialType) {
      id subject description status createdAt
    }
  }
`;

export const FULFILL_MATERIAL_REQUEST = gql`
  mutation FulfillMaterialRequest($requestId: ID!, $materialId: ID!) {
    fulfillMaterialRequest(requestId: $requestId, materialId: $materialId) { id status fulfilledBy }
  }
`;

// ─── CURATED PLAYLISTS ────────────────────────────────────────────────────────
const PLAYLIST_FIELDS = `id title description subject semester branch college youtubePlaylistUrl thumbnailUrl channelName videoCount totalDuration tags likes saves isCurated difficulty language hasLiked hasSaved createdAt curator { id name avatarUrl } videos { id youtubeUrl title duration thumbnailUrl orderNum }`;

export const PLAYLISTS = gql`
  query Playlists($subject: String, $semester: Int, $branch: String, $college: String, $search: String, $difficulty: String, $savedOnly: Boolean) {
    playlists(subject: $subject, semester: $semester, branch: $branch, college: $college, search: $search, difficulty: $difficulty, savedOnly: $savedOnly) { ${PLAYLIST_FIELDS} }
  }
`;

export const PLAYLIST = gql`
  query Playlist($id: ID!) { playlist(id: $id) { ${PLAYLIST_FIELDS} } }
`;

export const MY_SAVED_PLAYLISTS = gql`
  query MySavedPlaylists { mySavedPlaylists { ${PLAYLIST_FIELDS} } }
`;

export const CREATE_PLAYLIST = gql`
  mutation CreatePlaylist($title: String!, $description: String, $subject: String!, $semester: Int, $branch: String, $college: String, $youtubePlaylistUrl: String, $thumbnailUrl: String, $channelName: String, $videoCount: Int, $totalDuration: String, $tags: [String!], $difficulty: String, $language: String) {
    createPlaylist(title: $title, description: $description, subject: $subject, semester: $semester, branch: $branch, college: $college, youtubePlaylistUrl: $youtubePlaylistUrl, thumbnailUrl: $thumbnailUrl, channelName: $channelName, videoCount: $videoCount, totalDuration: $totalDuration, tags: $tags, difficulty: $difficulty, language: $language) {
      ${PLAYLIST_FIELDS}
    }
  }
`;

export const ADD_PLAYLIST_VIDEO = gql`
  mutation AddPlaylistVideo($playlistId: ID!, $youtubeUrl: String!, $title: String!, $duration: String, $thumbnailUrl: String, $orderNum: Int) {
    addPlaylistVideo(playlistId: $playlistId, youtubeUrl: $youtubeUrl, title: $title, duration: $duration, thumbnailUrl: $thumbnailUrl, orderNum: $orderNum) {
      id youtubeUrl title duration thumbnailUrl orderNum
    }
  }
`;

export const LIKE_PLAYLIST = gql`
  mutation LikePlaylist($playlistId: ID!) { likePlaylist(playlistId: $playlistId) }
`;

export const SAVE_PLAYLIST = gql`
  mutation SavePlaylist($playlistId: ID!) { savePlaylist(playlistId: $playlistId) }
`;

export const DELETE_PLAYLIST = gql`
  mutation DeletePlaylist($playlistId: ID!) { deletePlaylist(playlistId: $playlistId) }
`;

// ─── PG PARTNER ───────────────────────────────────────────────────────────────
const PG_FIELDS = `id listingType title description location nearCollege city budgetMin budgetMax preferredGender roomType occupancy moveInDate amenities preferences images contact contactWhatsapp status college interestCount hasShownInterest createdAt poster { id name email avatarUrl college year }`;

export const PG_LISTINGS = gql`
  query PgListings($college: String, $city: String, $budgetMax: Int, $preferredGender: String, $roomType: String, $listingType: String) {
    pgListings(college: $college, city: $city, budgetMax: $budgetMax, preferredGender: $preferredGender, roomType: $roomType, listingType: $listingType) { ${PG_FIELDS} }
  }
`;

export const PG_LISTING = gql`
  query PgListing($id: ID!) { pgListing(id: $id) { ${PG_FIELDS} } }
`;

export const MY_PG_LISTINGS = gql`
  query MyPgListings { myPgListings { ${PG_FIELDS} } }
`;

export const CREATE_PG_LISTING = gql`
  mutation CreatePgListing($listingType: String, $title: String!, $description: String, $location: String!, $nearCollege: String, $city: String, $budgetMin: Int, $budgetMax: Int!, $preferredGender: String, $roomType: String, $occupancy: Int, $moveInDate: String, $amenities: [String!], $preferences: [String!], $images: [String!], $contact: String, $contactWhatsapp: Boolean) {
    createPgListing(listingType: $listingType, title: $title, description: $description, location: $location, nearCollege: $nearCollege, city: $city, budgetMin: $budgetMin, budgetMax: $budgetMax, preferredGender: $preferredGender, roomType: $roomType, occupancy: $occupancy, moveInDate: $moveInDate, amenities: $amenities, preferences: $preferences, images: $images, contact: $contact, contactWhatsapp: $contactWhatsapp) {
      ${PG_FIELDS}
    }
  }
`;

export const UPDATE_PG_LISTING = gql`
  mutation UpdatePgListing($listingId: ID!, $title: String, $description: String, $budgetMax: Int, $status: String, $amenities: [String!], $images: [String!]) {
    updatePgListing(listingId: $listingId, title: $title, description: $description, budgetMax: $budgetMax, status: $status, amenities: $amenities, images: $images) {
      id status budgetMax
    }
  }
`;

export const DELETE_PG_LISTING = gql`
  mutation DeletePgListing($listingId: ID!) { deletePgListing(listingId: $listingId) }
`;

export const SHOW_PG_INTEREST = gql`
  mutation ShowPgInterest($listingId: ID!, $message: String) { showPgInterest(listingId: $listingId, message: $message) }
`;

export const WITHDRAW_PG_INTEREST = gql`
  mutation WithdrawPgInterest($listingId: ID!) { withdrawPgInterest(listingId: $listingId) }
`;

// ─── DISCUSSION FORUMS ────────────────────────────────────────────────────────
const REPLY_FIELDS = `id postId body upvotes isSolution isAnonymous parentReplyId hasVoted createdAt author { id name avatarUrl college }`;
const POST_FIELDS  = `id title body tags views upvotes replyCount isPinned isSolved isQuestion isAnonymous hasVoted hasSaved createdAt updatedAt forum { id name category college icon color } author { id name avatarUrl college } replies { ${REPLY_FIELDS} }`;

export const FORUMS = gql`
  query Forums($college: String, $category: ForumCategory, $global: Boolean) {
    forums(college: $college, category: $category, global: $global) { id name description category college icon color postCount isGlobal createdAt }
  }
`;

export const FORUM_POSTS = gql`
  query ForumPosts($forumId: ID!, $search: String, $tag: String, $sortBy: String) {
    forumPosts(forumId: $forumId, search: $search, tag: $tag, sortBy: $sortBy) { ${POST_FIELDS} }
  }
`;

export const FORUM_POST = gql`
  query ForumPost($id: ID!) { forumPost(id: $id) { ${POST_FIELDS} } }
`;

export const TRENDING_FORUM_POSTS = gql`
  query TrendingForumPosts($college: String) {
    trendingForumPosts(college: $college) { ${POST_FIELDS} }
  }
`;

export const MY_FORUM_POSTS = gql`
  query MyForumPosts { myForumPosts { ${POST_FIELDS} } }
`;

export const SAVED_FORUM_POSTS = gql`
  query SavedForumPosts { savedForumPosts { ${POST_FIELDS} } }
`;

export const CREATE_FORUM = gql`
  mutation CreateForum($name: String!, $description: String, $category: ForumCategory, $college: String, $icon: String, $color: String, $isGlobal: Boolean) {
    createForum(name: $name, description: $description, category: $category, college: $college, icon: $icon, color: $color, isGlobal: $isGlobal) {
      id name description category college icon color isGlobal createdAt
    }
  }
`;

export const CREATE_FORUM_POST = gql`
  mutation CreateForumPost($forumId: ID!, $title: String!, $body: String!, $tags: [String!], $isAnonymous: Boolean, $isQuestion: Boolean) {
    createForumPost(forumId: $forumId, title: $title, body: $body, tags: $tags, isAnonymous: $isAnonymous, isQuestion: $isQuestion) { ${POST_FIELDS} }
  }
`;

export const UPDATE_FORUM_POST = gql`
  mutation UpdateForumPost($postId: ID!, $title: String, $body: String, $tags: [String!]) {
    updateForumPost(postId: $postId, title: $title, body: $body, tags: $tags) { id title body tags updatedAt }
  }
`;

export const DELETE_FORUM_POST = gql`
  mutation DeleteForumPost($postId: ID!) { deleteForumPost(postId: $postId) }
`;

export const REPLY_TO_FORUM_POST = gql`
  mutation ReplyToForumPost($postId: ID!, $body: String!, $isAnonymous: Boolean, $parentReplyId: ID) {
    replyToForumPost(postId: $postId, body: $body, isAnonymous: $isAnonymous, parentReplyId: $parentReplyId) { ${REPLY_FIELDS} }
  }
`;

export const UPDATE_FORUM_REPLY = gql`
  mutation UpdateForumReply($replyId: ID!, $body: String!) {
    updateForumReply(replyId: $replyId, body: $body) { id body }
  }
`;

export const DELETE_FORUM_REPLY = gql`
  mutation DeleteForumReply($replyId: ID!) { deleteForumReply(replyId: $replyId) }
`;

export const VOTE_FORUM_POST = gql`
  mutation VoteForumPost($postId: ID!) { voteForumPost(postId: $postId) }
`;

export const VOTE_FORUM_REPLY = gql`
  mutation VoteForumReply($replyId: ID!) { voteForumReply(replyId: $replyId) }
`;

export const SAVE_FORUM_POST = gql`
  mutation SaveForumPost($postId: ID!) { saveForumPost(postId: $postId) }
`;

export const MARK_REPLY_SOLUTION = gql`
  mutation MarkReplySolution($replyId: ID!, $isSolution: Boolean!) {
    markReplySolution(replyId: $replyId, isSolution: $isSolution) { id isSolution }
  }
`;

export const PIN_FORUM_POST = gql`
  mutation PinForumPost($postId: ID!, $pinned: Boolean!) {
    pinForumPost(postId: $postId, pinned: $pinned) { id isPinned }
  }
`;


// ── Scholarship ───────────────────────────────────────────────────────────────
const SCHOLARSHIP_FIELDS = `
  id title description amount currency scholarshipType seats seatsFilled deadline
  minCgpa minAttendance maxBacklogs eligibleYears eligibleBranches eligibleColleges
  genderRequirement incomeLimit requiredSkills requiredDocuments
  essayRequired essayPrompt disbursementType selectionCriteria
  websiteUrl contactEmail isVerified college status applicationCount hasApplied createdAt
  offeredBy { id name avatarUrl type }
  meetsRequirements { cgpa attendance backlogs overall }
`;

export const GET_SCHOLARSHIPS = gql`
  query GetScholarships($type: String, $status: String, $search: String, $minAmount: Float, $maxAmount: Float) {
    scholarships(type: $type, status: $status, search: $search, minAmount: $minAmount, maxAmount: $maxAmount) { ${SCHOLARSHIP_FIELDS} }
  }
`;

export const GET_SCHOLARSHIP = gql`
  query GetScholarship($id: ID!) {
    scholarship(id: $id) { ${SCHOLARSHIP_FIELDS} }
  }
`;

export const MY_SCHOLARSHIPS = gql`
  query MyScholarships { myScholarships { ${SCHOLARSHIP_FIELDS} } }
`;

export const MY_SCHOLARSHIP_APPLICATIONS = gql`
  query MyScholarshipApplications {
    myScholarshipApplications {
      id essay documents cgpaSnapshot attendanceSnapshot status adminNote appliedAt reviewedAt
      scholarship { ${SCHOLARSHIP_FIELDS} }
      applicant { id name avatarUrl college branch year }
    }
  }
`;

export const SCHOLARSHIP_APPLICANTS = gql`
  query ScholarshipApplicants($scholarshipId: ID!) {
    scholarshipApplicants(scholarshipId: $scholarshipId) {
      id essay documents cgpaSnapshot attendanceSnapshot status adminNote appliedAt reviewedAt
      applicant { id name avatarUrl college branch year cgpa }
    }
  }
`;

export const CREATE_SCHOLARSHIP = gql`
  mutation CreateScholarship(
    $title: String! $description: String! $amount: Float! $currency: String
    $scholarshipType: String $seats: Int $deadline: String!
    $minCgpa: Float $minAttendance: Float $maxBacklogs: Int
    $eligibleYears: [Int!] $eligibleBranches: [String!] $eligibleColleges: [String!]
    $genderRequirement: String $incomeLimit: Float
    $requiredSkills: [String!] $requiredDocuments: [String!]
    $essayRequired: Boolean $essayPrompt: String
    $disbursementType: String $selectionCriteria: String
    $websiteUrl: String $contactEmail: String $college: String
    $offererName: String $offererEmail: String
  ) {
    createScholarship(
      title: $title description: $description amount: $amount currency: $currency
      scholarshipType: $scholarshipType seats: $seats deadline: $deadline
      minCgpa: $minCgpa minAttendance: $minAttendance maxBacklogs: $maxBacklogs
      eligibleYears: $eligibleYears eligibleBranches: $eligibleBranches eligibleColleges: $eligibleColleges
      genderRequirement: $genderRequirement incomeLimit: $incomeLimit
      requiredSkills: $requiredSkills requiredDocuments: $requiredDocuments
      essayRequired: $essayRequired essayPrompt: $essayPrompt
      disbursementType: $disbursementType selectionCriteria: $selectionCriteria
      websiteUrl: $websiteUrl contactEmail: $contactEmail college: $college
      offererName: $offererName offererEmail: $offererEmail
    ) { ${SCHOLARSHIP_FIELDS} }
  }
`;

export const UPDATE_SCHOLARSHIP = gql`
  mutation UpdateScholarship($scholarshipId: ID!, $status: String, $seats: Int, $deadline: String, $description: String) {
    updateScholarship(scholarshipId: $scholarshipId, status: $status, seats: $seats, deadline: $deadline, description: $description) { id status seats deadline description }
  }
`;

export const DELETE_SCHOLARSHIP = gql`
  mutation DeleteScholarship($scholarshipId: ID!) { deleteScholarship(scholarshipId: $scholarshipId) }
`;

export const APPLY_FOR_SCHOLARSHIP = gql`
  mutation ApplyForScholarship($scholarshipId: ID!, $essay: String, $documents: [String!]) {
    applyForScholarship(scholarshipId: $scholarshipId, essay: $essay, documents: $documents) {
      id status appliedAt
      scholarship { id title amount currency }
    }
  }
`;

export const WITHDRAW_SCHOLARSHIP_APPLICATION = gql`
  mutation WithdrawScholarshipApplication($applicationId: ID!) { withdrawScholarshipApplication(applicationId: $applicationId) }
`;

export const REVIEW_SCHOLARSHIP_APPLICATION = gql`
  mutation ReviewScholarshipApplication($applicationId: ID!, $status: String!, $adminNote: String) {
    reviewScholarshipApplication(applicationId: $applicationId, status: $status, adminNote: $adminNote) {
      id status adminNote reviewedAt
    }
  }
`;

// ─── Admin Console ────────────────────────────────────────────────────────────
export const ADMIN_STATS = gql`
  query AdminStats { adminStats { key label total today } }
`;

export const ADMIN_RECENT_USERS = gql`
  query AdminRecentUsers($limit: Int) {
    adminRecentUsers(limit: $limit) { id name email college branch createdAt }
  }
`;

export const ADMIN_SOCIAL_FEED = gql`
  query AdminSocialFeed($limit: Int) {
    adminSocialFeed(limit: $limit) {
      id postType title body url tags likes createdAt
      author { id name college }
      comments { id }
    }
  }
`;

export const ADMIN_DELETE_SOCIAL_POST = gql`
  mutation AdminDeleteSocialPost($postId: ID!) { adminDeleteSocialPost(postId: $postId) }
`;

export const MONITOR_SNAPSHOT = gql`
  query MonitorSnapshot {
    monitorSnapshot {
      serverTime uptimeSeconds nodeVersion env memoryRssMb heapUsedMb
      dbName dbSizeMb gatedDomains gatedResolvers
    }
  }
`;

export const DISABLED_FEATURES = gql`
  query DisabledFeatures { disabledFeatures }
`;
export const ADMIN_FEATURE_GATES = gql`
  query AdminFeatureGates { adminFeatureGates { domain disabled } }
`;
export const SET_FEATURE_GATE = gql`
  mutation SetFeatureGate($domain: String!, $disabled: Boolean!) {
    setFeatureGate(domain: $domain, disabled: $disabled)
  }
`;
export const LOGIN_ADMIN_WITH_GOOGLE = gql`
  mutation LoginAdminWithGoogle($idToken: String!) {
    loginAdminWithGoogle(idToken: $idToken) {
      token kind
      superAdmin { id name email }
      admin { id name email college approved rejectionNote }
    }
  }
`;

export const UPDATE_PROFILE = gql`
  mutation UpdateProfile($name: String, $bio: String, $skills: [String!], $branch: String, $year: Int, $college: String) {
    updateProfile(name: $name, bio: $bio, skills: $skills, branch: $branch, year: $year, college: $college) {
      id name branch year college bio
    }
  }
`;
export const CHANGE_PASSWORD = gql`
  mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
    changePassword(currentPassword: $currentPassword, newPassword: $newPassword)
  }
`;

export const MY_YEAR_TARGET = gql`
  query MyYearTarget {
    myYearTarget {
      id title vision startYear targetYear
      overallProgress totalGoals completedGoals overdueGoals
      milestones {
        id yearNum label progress overdueCount
        goals {
          id title description category priority dueDate status isOverdue completedAt createdAt
        }
      }
    }
  }
`;

export const UPSERT_YEAR_TARGET = gql`
  mutation UpsertYearTarget($title: String!, $vision: String, $startYear: Int!, $targetYear: Int!) {
    upsertYearTarget(title: $title, vision: $vision, startYear: $startYear, targetYear: $targetYear) {
      id title vision startYear targetYear overallProgress totalGoals completedGoals overdueGoals
      milestones { id yearNum label progress overdueCount goals { id title description category priority dueDate status isOverdue completedAt createdAt } }
    }
  }
`;

export const ADD_MILESTONE = gql`
  mutation AddMilestone($targetId: ID!, $yearNum: Int!, $label: String!) {
    addMilestone(targetId: $targetId, yearNum: $yearNum, label: $label) {
      id yearNum label progress overdueCount goals { id title description category priority dueDate status isOverdue completedAt createdAt }
    }
  }
`;

export const ADD_GOAL = gql`
  mutation AddGoal($milestoneId: ID!, $title: String!, $description: String, $category: String, $priority: String, $dueDate: String) {
    addGoal(milestoneId: $milestoneId, title: $title, description: $description, category: $category, priority: $priority, dueDate: $dueDate) {
      id title description category priority dueDate status isOverdue createdAt
    }
  }
`;

export const UPDATE_GOAL = gql`
  mutation UpdateGoal($goalId: ID!, $title: String, $description: String, $category: String, $priority: String, $dueDate: String, $status: String) {
    updateGoal(goalId: $goalId, title: $title, description: $description, category: $category, priority: $priority, dueDate: $dueDate, status: $status) {
      id title description category priority dueDate status isOverdue completedAt
    }
  }
`;

export const DELETE_GOAL = gql`
  mutation DeleteGoal($goalId: ID!) {
    deleteGoal(goalId: $goalId)
  }
`;

export const DELETE_MILESTONE = gql`
  mutation DeleteMilestone($milestoneId: ID!) {
    deleteMilestone(milestoneId: $milestoneId)
  }
`;
